import { rewriteApiUrl } from '../_util/data-target';
import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Attachment, FileUploadService, isAttachmentImage } from './file-upload.service';
import { ApiResponse } from './data.service';
import { DataOfflineService } from './data-offline.service';
import { environment } from '../../environments/environment';

export interface ImageNodeAttachmentMetadata {
  attachmentId: string;
  src: string;
  filename?: string;
  fileSizeBytes?: number;
  naturalWidth?: number;
  naturalHeight?: number;
  mimeType?: string;
  imageSourceType: 'attachment';
  isImage?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private readonly baseUrl = environment.apiBaseUrl || '/api';

  constructor(
    private fileUpload: FileUploadService,
    private offline: DataOfflineService,
  ) {}

  uploadImage(file: File, noteId?: string, spaceId?: string): Observable<ImageNodeAttachmentMetadata> {
    const safeNoteId = String(noteId ?? '').trim();
    if (!safeNoteId) {
      return throwError(() => new Error('A noteId is required to upload an image with the existing attachment API.'));
    }

    return this.isUnsavedNote(safeNoteId).pipe(
      // A note that isn't saved yet (a local draft, or not even autosaved) keeps its uploads local
      // until its first explicit save — same as the note itself (see Note.localDraft); the sync
      // push then uploads them into the Space the note was saved in.
      switchMap((unsaved) =>
        unsaved
          ? this.fileUpload.queueAttachmentUpload(file, safeNoteId)
          : this.fileUpload.uploadAttachment(file, safeNoteId, spaceId),
      ),
      switchMap((response: ApiResponse<Attachment>) => {
        if (response.status !== 'success' || !response.data) {
          return throwError(() => new Error(response.error?.message ?? 'Image upload failed.'));
        }

        const metadata = this.mapAttachmentToImageMetadata(response.data, spaceId);
        if (!metadata) {
          return throwError(() => new Error('Uploaded attachment is not a valid image.'));
        }

        return of(metadata);
      }),
    );
  }

  private isUnsavedNote(noteId: string): Observable<boolean> {
    return from(this.offline.getNote(noteId)).pipe(
      map((note) => !note || note.localDraft === true),
      catchError(() => of(false)),
    );
  }

  /** Image attachments of a note (online: the server's list for the note; offline: the local cache). */
  getNoteImages(noteId: string, spaceId?: string): Observable<ImageNodeAttachmentMetadata[]> {
    const safeNoteId = String(noteId ?? '').trim();
    if (!safeNoteId) {
      return of([]);
    }
    return this.fileUpload.getFiles(safeNoteId, spaceId).pipe(
      map((attachments) =>
        (attachments ?? [])
          .map((attachment) => this.mapAttachmentToImageMetadata(attachment, spaceId))
          .filter((meta): meta is ImageNodeAttachmentMetadata => !!meta),
      ),
      catchError(() => of([])),
    );
  }

  /** Metadata of one image attachment; with noteId it's looked up in that note's attachment list first
   * (the source with filename and dimensions), else from the attachment record / local cache. */
  getImageMetadata(attachmentId: string, spaceId?: string, noteId?: string): Observable<ImageNodeAttachmentMetadata | null> {
    const safeAttachmentId = String(attachmentId ?? '').trim();
    if (!safeAttachmentId) {
      return of(null);
    }

    const fromNote$ = noteId
      ? this.getNoteImages(noteId, spaceId).pipe(map((images) => images.find((img) => img.attachmentId === safeAttachmentId) ?? null))
      : of(null);
    return fromNote$.pipe(
      switchMap((found) => (found ? of(found) : this.getAttachmentRecordMetadata(safeAttachmentId, spaceId))),
    );
  }

  private getAttachmentRecordMetadata(safeAttachmentId: string, spaceId?: string): Observable<ImageNodeAttachmentMetadata | null> {
    return this.fileUpload.getAttachmentInfo(safeAttachmentId).pipe(
      map((attachment) => {
        if (!attachment) {
          return null;
        }
        return this.mapAttachmentToImageMetadata({
          ...attachment,
          _id: String(attachment._id ?? safeAttachmentId),
        } as Attachment, spaceId);
      }),
    );
  }

  private mapAttachmentToImageMetadata(attachment: Attachment, spaceId?: string): ImageNodeAttachmentMetadata | null {
    if (!attachment?._id || !isAttachmentImage(attachment)) {
      return null;
    }

    const naturalWidth = this.toPositiveNumber(attachment.naturalWidth ?? attachment.imageWidth);
    const naturalHeight = this.toPositiveNumber(attachment.naturalHeight ?? attachment.imageHeight);
    const fileSizeBytes = this.toPositiveNumber(attachment.fileSizeBytes ?? attachment.size);
    const src = this.resolveAttachmentSrc(attachment, spaceId);

    const filename = String(attachment.filename ?? '').trim();
    return {
      attachmentId: attachment._id,
      src,
      // normalizeAttachment() falls back to the id when the record has no filename.
      filename: filename && filename !== attachment._id ? filename : undefined,
      fileSizeBytes,
      naturalWidth,
      naturalHeight,
      mimeType: attachment.mimeType,
      imageSourceType: 'attachment',
      isImage: attachment.isImage === true ? true : undefined,
    };
  }

  private resolveAttachmentSrc(attachment: Attachment, spaceId?: string): string {
    const candidate = String(attachment.remoteUrl ?? attachment.localUrl ?? '').trim();
    if (candidate) {
      return rewriteApiUrl(candidate);
    }
    const spaceQuery = spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : '';
    return rewriteApiUrl(`${this.baseUrl}/attachments/${encodeURIComponent(attachment._id)}/file${spaceQuery}`);
  }

  private toPositiveNumber(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }
}
